import { TripDateField, monthText, type DatePrecision } from "../../components/TripDateField";
import { CaseChat } from "./CaseChat";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  CalendarDays,
  ChevronDown,
  Check,
  CheckCircle2,
  Circle,
  ClipboardList,
  Clock,
  Compass,
  Download,
  ExternalLink,
  FileText,
  FolderOpen,
  Flag,
  History,
  Mail,
  MessageCircle,
  PenLine,
  Phone,
  RefreshCw,
  StickyNote,
  Target,
  Ticket,
  UserRound,
  Users,
  Wallet,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ApiError, erpApi, useDevelopmentFixtures } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { BookingActions } from "../../components/BookingActions";
import { CustomerDialogHost } from "../../components/CustomerDialog";
import {
  BookingDialog,
  LeadDialog,
  OrderDialog,
} from "../../components/OperationalDetails";
import {
  Button,
  Dialog,
  Field,
  StatePanel,
  StatusBadge,
} from "../../components/ui";
import { crmApi, type CrmStage } from "../../data/crm";
import { useErpData } from "../../data/DataProvider";
import { formatDate, formatDateTime, mexicoDateKey } from "../../data/format";
import { viewCache } from "../../data/view-cache";
import type { Booking, Lead, ServiceType } from "../../data/types";
import {
  commerceApi,
  workspaceApi,
  type CaseDossier,
  type CaseEntity,
  type CaseKind,
} from "../../data/workspace";
import { NextActionDialog } from "./NextActionDialog";
import { ConfirmOrderPayment } from "./ConfirmOrderPayment";
import { PaymentDocuments } from "./PaymentDocuments";
import { CaseConfirmation, hasAgreedQuote } from "./CaseConfirmation";
import { CaseSheet } from "./CaseSheet";
import { useFitToViewport } from "./useFitToViewport";
import { CasePayments } from "./CasePayments";
import { BookingChanges } from "./BookingChanges";
import { BalanceLink } from "./BalanceLink";
import { QuotePanel } from "./QuotePanel";
import { serviceIcons } from "./ServiceTag";
import {
  copyLabel,
  money,
  safeReturn,
  funnelUrl,
  serviceNames,
  stageNames,
} from "./shared";

/** Parts of the case shown in the case file sheet ("Abrir expediente"), e.g. from an alert. */
const sheetSections = ["Reserva y pagos", "Participantes", "Documentos"];
const caseKinds = ["opportunity", "booking", "order", "lead"];
/** Frequent reasons; "Solo información" replaces the old "solo consulta" classification. */
const lostReasons = ["Solo información", "Precio", "Fecha no disponible", "Eligió otra opción", "Sin respuesta"];
const dossierCache = viewCache<CaseDossier>(30);
/** The one progress bar of a case: the sale's stages, then "Pagado" and "Realizado" with its booking. */
const saleSteps: Array<{ stage: string; icon: LucideIcon }> = [
  { stage: "new", icon: Target },
  { stage: "conversation", icon: MessageCircle },
  { stage: "quote", icon: FileText },
  { stage: "awaiting", icon: Clock },
  { stage: "won", icon: Ticket },
  { stage: "paid", icon: Wallet },
  { stage: "done", icon: Flag },
];
/** Stages the team may set by hand; "Pagado" and "Realizado" come from the booking. */
const manualStages = ["new", "conversation", "quote", "awaiting", "won", "lost"];
/** Where the case is: the sale's stage, moved on to "Pagado" and "Realizado" by its booking. */
export function caseStage(dossier: CaseDossier, today = mexicoDateKey()): string {
  const stage = String(dossier.opportunity?.stage ?? "");
  if (stage === "lost") return "lost";
  const active = (dossier.bookings ?? []).filter((item) => !["cancelled", "canceled", "expired", "failed"].includes(String(item.status)));
  if (stage === "won" || active.length) {
    const done =
      dossier.status.operational === "completed" ||
      active.some((item) => item.attendance_status === "attended" || (typeof item.booking_date === "string" && item.booking_date.slice(0, 10) < today));
    const paid = active.some((item) => {
      const summary = (item.payment_summary ?? {}) as Record<string, unknown>;
      return Number(summary.contract_total ?? 0) > 0 && Number(summary.balance_due ?? 1) <= 0;
    });
    return done ? "done" : paid ? "paid" : "won";
  }
  if (stage) return stage;
  const lead = String(dossier.leads?.[0]?.status ?? "");
  return lead && lead !== "new" ? "conversation" : "new";
}
function activityIcon(type: string): LucideIcon {
  if (type.includes("message") || type.includes("contact")) return MessageCircle;
  if (type.includes("note")) return StickyNote;
  if (type.includes("followup")) return CalendarClock;
  if (type.includes("stage") || type.includes("conversion")) return ArrowRight;
  if (type.includes("draft")) return PenLine;
  if (type.includes("customer")) return UserRound;
  return Circle;
}
export function CasePage() {
  const { kind = "", id = "" } = useParams();
  return <CaseView kind={kind} id={id} />;
}

/**
 * The case itself: header, WhatsApp chat and essentials. Mensajes embeds it beside the inbox
 * (`embedded`, with the selected conversation), so both screens are the same view.
 */
export function CaseView({
  kind,
  id,
  embedded = false,
  conversationId,
}: {
  kind: string;
  id: string;
  embedded?: boolean;
  conversationId?: string | null;
}) {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { snapshot, refresh, executeCommand } = useErpData();
  const { hasPermission } = useAuth();
  const [remoteBooking, setRemoteBooking] = useState<Booking | null>(null);
  const [bookingError, setBookingError] = useState("");
  const [bookingPending, setBookingPending] = useState(false);
  const [dossier, setDossier] = useState<CaseDossier | null>(null);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [revision, setRevision] = useState(0);
  const [detail, setDetail] = useState<"booking" | "lead" | "order" | null>(
    null,
  );
  const [feedback, setFeedback] = useState("");
  const [working, setWorking] = useState(false);
  const loadedCase = useRef("");
  const [mobileView, setMobileView] = useState<"main" | "side">("main");
  // The main area shows the chat, or the quotes while quoting; everything else lives in the case file.
  const requestedSection = params.get("section") ?? "";
  const section = requestedSection === "Cotizaciones" ? "Cotizaciones" : "Resumen";
  const updateSection = (value: string) => {
    const next = new URLSearchParams(params);
    next.set("section", value);
    next.delete("nueva");
    setParams(next, { replace: true });
  };
  // "view" or, to fill missing data (e.g. before a payment), "edit".
  const [sheetMode, setSheetMode] = useState<"" | "view" | "edit">("");
  const [sheetFocus, setSheetFocus] = useState("");
  const [stageOpen, setStageOpen] = useState(false);
  // The phone's bottom bar asks the payments card to start "Registrar pago".
  const [payRequest, setPayRequest] = useState(0);
  const openSheet = (focus = "") => {
    setSheetFocus(focus);
    setSheetMode("view");
  };
  const openSection = (name: string) => {
    if (name === "Cotizaciones") updateSection(name);
    else if (sheetSections.includes(name)) openSheet(name);
  };
  // Old links to a part of the case (e.g. ?section=Documentos) open it in the case file.
  useEffect(() => {
    if (!sheetSections.includes(requestedSection)) return;
    openSheet(requestedSection);
    const next = new URLSearchParams(params);
    next.delete("section");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSection]);
  const load = () => {
    setRevision((v) => v + 1);
  };
  useEffect(() => {
    let active = true;
    setError("");
    setForbidden(false);
    if (!caseKinds.includes(kind)) {
      setError("Este tipo de expediente no existe.");
      return;
    }
    // Show the local dossier at once; WordPress quotes (~1.5 s) arrive with the full read.
    let full = false;
    if (loadedCase.current !== `${kind}:${id}`)
      workspaceApi
        .caseDetail(kind as CaseKind, id, { deferQuotes: true })
        .then((value) => {
          if (active && !full) setDossier(value);
        })
        .catch(() => undefined);
    workspaceApi
      .caseDetail(kind as CaseKind, id)
      .then((value) => {
        full = true;
        dossierCache.set(`${kind}:${id}`, value);
        if (active) {
          loadedCase.current = `${kind}:${id}`;
          setDossier(value);
        }
      })
      .catch((caught) => {
        if (active) {
          setError(
            caught instanceof Error
              ? caught.message
              : "No pudimos abrir el expediente.",
          );
          const denied = caught instanceof ApiError && caught.status === 403;
          setForbidden(denied);
          if (denied) {
            dossierCache.delete(`${kind}:${id}`);
            setDossier(null);
            setRemoteBooking(null);
            setDetail(null);
          }
        }
      });
    return () => {
      active = false;
    };
  }, [kind, id, revision, snapshot?.generatedAt]);
  useEffect(() => {
    loadedCase.current = "";
    setDossier(dossierCache.get(`${kind}:${id}`) ?? null);
    setDetail(null);
    setFeedback("");
    if (!embedded) window.scrollTo({ top: 0, behavior: "instant" });
  }, [kind, id]);
  const bookingId = dossier?.associatedIds.booking[0];
  // A booking just created in WordPress takes a few seconds to reach the panel: ask again by itself.
  const pendingRetries = useRef(0);
  useEffect(() => {
    if (useDevelopmentFixtures || !bookingId) return;
    let active = true;
    setBookingError("");
    setBookingPending(false);
    erpApi
      .bookingDetail(bookingId)
      .then((value) => {
        if (active) {
          pendingRetries.current = 0;
          setRemoteBooking(value);
        }
      })
      .catch((caught) => {
        if (active) {
          const message =
            caught instanceof Error
              ? caught.message
              : "No pudimos cargar las acciones de la reserva.";
          if (
            caught instanceof ApiError &&
            caught.status === 404 &&
            dossier?.quotes.some(
              (quote) =>
                quote.status === "converted" &&
                String(quote.booking_id) === bookingId,
            )
          ) {
            setBookingPending(true);
            if (pendingRetries.current < 10) {
              pendingRetries.current += 1;
              window.setTimeout(() => {
                if (active) load();
              }, 4000);
            }
          } else setBookingError(message);
          if (caught instanceof ApiError && caught.status === 403) {
            dossierCache.clear();
            setRemoteBooking(null);
            setDossier(null);
            setDetail(null);
            setForbidden(true);
            setError(message);
          }
        }
      });
    return () => {
      active = false;
    };
  }, [bookingId, revision, snapshot]);
  useEffect(() => {
    setRemoteBooking(null);
  }, [bookingId]);
  const returnTo = safeReturn(
    params.get("return"),
    dossier?.service ? funnelUrl(dossier.service) : "/",
  );
  const back = (
    <Link className="case-back" to={returnTo}>
      <ArrowLeft size={16} />{" "}
      {returnTo === "/"
        ? "Volver a Hoy"
        : returnTo.startsWith("/agenda")
          ? "Volver a la agenda"
          : returnTo.startsWith("/mensajes") || returnTo.startsWith("/bandeja")
            ? "Volver a mensajes"
            : returnTo.startsWith("/embudo")
              ? "Volver al embudo"
              : "Volver al listado"}
    </Link>
  );
  // The chat and the side column fill exactly the space left below the case header.
  // A sale with its WhatsApp chat is worked in Mensajes (chat, case and file in one screen):
  // links from the funnel, Hoy or the calendar open it there instead of a separate page.
  const navigate = useNavigate();
  useEffect(() => {
    if (embedded || useDevelopmentFixtures || !dossier || dossier.key.kind !== "opportunity" || !dossier.whatsappConversationId) return;
    const next = new URLSearchParams({ open: dossier.whatsappConversationId });
    if (dossier.customer.phone) next.set("phone", dossier.customer.phone);
    navigate(`/mensajes?${next}`, { replace: true });
  }, [embedded, dossier, navigate]);
  const layoutRef = useRef<HTMLDivElement>(null);
  useFitToViewport(layoutRef, [Boolean(dossier), embedded]);
  if (error && !dossier)
    return (
      <>
        {back}
        <StatePanel
          state={forbidden ? "forbidden" : "error"}
          description={error}
          onRetry={load}
        />
      </>
    );
  if (!dossier)
    return <StatePanel state="loading" title="Abriendo expediente" />;
  const booking =
    remoteBooking ??
    snapshot?.bookings.find((item) =>
      dossier.associatedIds.booking.includes(item.id),
    );
  const order = snapshot?.orders.find((item) =>
    dossier.associatedIds.order.includes(item.id),
  );
  const lead =
    dossier.leads
      .map((item) => leadFromCase(item, dossier))
      .find((item): item is Lead => item !== null) ??
    snapshot?.leads.find((item) =>
      dossier.associatedIds.lead.includes(item.id),
    );
  const confirmation = snapshot?.confirmations.find(
    (item) => item.bookingReference === booking?.reference,
  );
  const source =
    dossier.bookings[0] ??
    dossier.opportunity ??
    dossier.leads[0] ??
    dossier.orders[0];
  const latestQuote = [...dossier.quotes].sort((a, b) =>
    b.updated_at.localeCompare(a.updated_at),
  )[0];
  const root: CaseEntity | undefined = source
    ? {
        ...source,
        booking_date:
          source.booking_date ?? source.requested_date ?? latestQuote?.date,
        booking_time: source.booking_time ?? latestQuote?.time,
        people_count: source.people_count ?? latestQuote?.people,
      }
    : latestQuote
      ? {
          id: latestQuote.id,
          booking_date: latestQuote.date,
          booking_time: latestQuote.time,
          people_count: latestQuote.people,
        }
      : undefined;
  const stage = caseStage(dossier);
  const stepIndex = saleSteps.findIndex((step) => step.stage === stage);
  const messageParams = new URLSearchParams();
  if (dossier.customer.id) messageParams.set("customer", dossier.customer.id);
  else if (dossier.customer.phone)
    messageParams.set("phone", dossier.customer.phone);
  if (dossier.associatedIds.opportunity[0])
    messageParams.set("opportunity", dossier.associatedIds.opportunity[0]);
  messageParams.set("return", location.pathname + location.search);
  const paymentRow = dossier.permissions.finance ? dossier.bookings[0] : undefined;
  const summary = (paymentRow?.payment_summary ?? null) as Record<string, unknown> | null;
  const money_ =
    summary && number(summary.contract_total) !== null
      ? {
          total: number(summary.contract_total)!,
          paid: number(summary.paid_amount) ?? 0,
          balance: number(summary.balance_due) ?? 0,
          currency: text(paymentRow?.currency, "MXN"),
        }
      : null;
  // Only people with a name: blank places (one per student or seat) are not participants.
  const participants = dossier.participants.filter((item) => text(item.name, "").trim() !== "");
  const attend = async () => {
    if (!booking || working) return;
    setWorking(true);
    setFeedback("");
    try {
      await executeCommand({
        type: "booking.mark_attended",
        aggregateId: booking.id,
        expectedVersion: booking.version,
        payload: {},
      });
      setFeedback("Asistencia registrada y confirmada por WordPress.");
      load();
    } catch (caught) {
      setFeedback(
        caught instanceof Error
          ? caught.message
          : "No se pudo registrar la asistencia.",
      );
    } finally {
      setWorking(false);
    }
  };
  const ServiceIcon = dossier.service ? serviceIcons[dossier.service] : Compass;
  // WooCommerce admin link of the order still waiting for payment (pending or transfer reported).
  // Order still waiting for payment (pending or transfer reported): its payment is confirmed from here.
  const unpaidOrder = dossier.orders.find((order) => ["pending", "on-hold"].includes(String(order.status)));
  const unpaidOrderId = unpaidOrder ? Number(unpaidOrder.source_id ?? unpaidOrder.id) : undefined;
  const unpaidAmount = unpaidOrder && dossier.permissions.finance ? money(number(unpaidOrder.total), text(unpaidOrder.currency, "MXN")) : undefined;
  const lost = dossier.opportunity?.stage === "lost";
  // A sale won with WordPress's confirmed payment no longer changes stage by hand.
  const officialWin = dossier.opportunity?.stage === "won" && dossier.opportunity.won_source === "wordpress";
  const canChangeStage = Boolean(dossier.opportunity && hasPermission("crm.write") && !officialWin);
  const canQuote = dossier.permissions.crm || dossier.quotes.length > 0;
  // "Cotizar" opens the quotes in the main area; with none yet, a new quote starts right away.
  const openQuotes = () => {
    const next = new URLSearchParams(params);
    next.set("section", "Cotizaciones");
    if (!dossier.quotes.length && dossier.permissions.commerceWrite) next.set("nueva", "1");
    else next.delete("nueva");
    setParams(next, { replace: true });
    setMobileView("main");
  };
  const caseConversation = conversationId ?? dossier.whatsappConversationId ?? null;
  const canPay = booking && dossier.bookings[0]
    ? dossier.permissions.finance && hasPermission("bookings.record_payment")
    : dossier.permissions.finance && dossier.permissions.commerceWrite;
  const initials = dossier.customer.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toLocaleUpperCase("es-MX"))
    .join("");
  return (
    <section className="case-page">
      {!embedded && back}
      <div className="case-header-card">
      <header className="case-hero">
        <span className="case-avatar" aria-hidden="true">
          {initials || <UserRound size={22} />}
        </span>
        <div className="case-hero__main">
          {dossier.opportunity && !dossier.bookings.length && hasPermission("crm.write") ? (
            <ServicePicker
              service={dossier.service ?? null}
              opportunity={{ id: dossier.opportunity.id, version: Number(dossier.opportunity.version) }}
              onSaved={(name) => {
                setFeedback(`Venta movida a ${name}.`);
                load();
              }}
            />
          ) : (
            <p className={`case-hero__service case-hero__service--${dossier.service ?? "none"}`}>
              <ServiceIcon size={15} aria-hidden="true" />
              {dossier.service ? serviceNames[dossier.service] : "Servicio por definir"}
            </p>
          )}
          <h1>{dossier.title}</h1>
          <ul className="case-hero__facts" aria-label="Datos del viaje">
            <TripFacts
              date={typeof (root?.booking_date ?? root?.requested_date) === "string" ? String(root?.booking_date ?? root?.requested_date).slice(0, 10) : ""}
              people={Number(root?.people_count) || null}
              school={dossier.service === "school"}
              teachers={dossier.opportunity?.teachers_count == null ? null : Number(dossier.opportunity.teachers_count)}
              companions={dossier.opportunity?.companions_count == null ? null : Number(dossier.opportunity.companions_count)}
              precision={!dossier.bookings.length && dossier.opportunity?.requested_date_precision === "month" ? "month" : "day"}
              opportunity={
                dossier.opportunity && !dossier.bookings.length && hasPermission("crm.write")
                  ? { id: dossier.opportunity.id, version: Number(dossier.opportunity.version) }
                  : null
              }
              onSaved={load}
            />
            {typeof root?.booking_time === "string" && root.booking_time && (
              <li>
                <Clock size={16} aria-hidden="true" />
                {root.booking_time.slice(0, 5)}
              </li>
            )}
            {money_ && (
              <li className={`case-hero__money${money_.balance > 0 ? " has-balance" : ""}`}>
                <Wallet size={16} aria-hidden="true" />
                Pagado {money(money_.paid, money_.currency)} de {money(money_.total, money_.currency)}
                {money_.balance > 0 && <strong> · Saldo {money(money_.balance, money_.currency)}</strong>}
              </li>
            )}
            {dossier.customer.phone && (
              <li>
                <Phone size={16} aria-hidden="true" />
                <a href={`tel:${dossier.customer.phone}`}>{dossier.customer.phone}</a>
              </li>
            )}
            {dossier.customer.email && (
              <li>
                <Mail size={16} aria-hidden="true" />
                <a href={`mailto:${dossier.customer.email}`}>{dossier.customer.email}</a>
              </li>
            )}
          </ul>
        </div>
        <div className="case-hero__actions">
          {/* The case file in one sheet: customer, trip, group, sale, money, quotes, booking and documents. */}
          <button type="button" className="button button--quiet case-hero__open" onClick={() => openSheet()}>
            <FolderOpen size={17} aria-hidden="true" /> Abrir expediente
          </button>
          {!embedded && hasPermission("whatsapp.read") && (
            <Link
              className="button button--quiet"
              to={`/mensajes?${messageParams}`}
              aria-label="Abrir mensajes del cliente"
            >
              <MessageCircle size={17} aria-hidden="true" /> Bandeja
            </Link>
          )}

          <button
            type="button"
            className="icon-button"
            aria-label="Actualizar"
            title="Actualizar"
            onClick={() => {
              void refresh();
              load();
            }}
          >
            <RefreshCw size={18} aria-hidden="true" />
          </button>
        </div>
      </header>
      <section className="case-journey" aria-label="Etapa de la venta">
        <ol>
          {saleSteps.map(({ stage: step, icon: Icon }, i) => (
            <li
              key={step}
              className={i < stepIndex ? "is-done" : i === stepIndex ? "is-current" : ""}
              aria-current={i === stepIndex ? "step" : undefined}
            >
              <span className="case-journey__dot" aria-hidden="true">
                {i < stepIndex ? <Check size={15} /> : <Icon size={15} />}
              </span>
              <span className="case-journey__label">{stageNames[step]}</span>
            </li>
          ))}
        </ol>
        {canChangeStage && (
          <button type="button" className="case-journey__change" onClick={() => setStageOpen(true)}>
            Cambiar etapa
          </button>
        )}
        {lost && (
          <p className="case-journey__lost">
            <XCircle size={16} aria-hidden="true" /> Venta perdida
            {typeof dossier.opportunity?.lost_reason === "string" && dossier.opportunity.lost_reason
              ? `: ${dossier.opportunity.lost_reason}`
              : ""}
          </p>
        )}
      </section>
      </div>
      {error && (
        <StatePanel
          state={forbidden ? "forbidden" : "error"}
          description={error}
          onRetry={load}
        />
      )}
      {dossier.orders.some((order) => order.status === "on-hold") && (
        // The customer used "Ya hice mi transferencia" on the proposal: the order waits for the deposit.
        <p className="inline-notice inline-notice--warning" role="status">
          <strong>El cliente avisó que ya transfirió.</strong> Revisa tu cuenta y, cuando veas el depósito, confírmalo aquí con <strong>Confirmar pago recibido</strong>.
        </p>
      )}
      {bookingPending && (
        <p className="quote-feedback" role="status">
          Reserva creada. Sus datos terminan de llegar al panel en unos segundos.
        </p>
      )}
      {bookingError && (
        <StatePanel state="error" description={bookingError} onRetry={load} />
      )}
      {feedback && (
        <p className="quote-feedback" role="status">
          {feedback}
        </p>
      )}
      {!!dossier.alerts?.length && (
        <section className="case-alerts" aria-label="Pendientes del expediente">
          {dossier.alerts.map((alert) => (
            <article
              key={alert.code}
              className={`case-alert case-alert--${alert.tone}`}
            >
              <AlertTriangle size={18} aria-hidden="true" />
              <div>
                <strong>{alert.title}</strong>
                <p>{alert.detail}</p>
              </div>
              {/* The unpaid WooCommerce order is confirmed from the panel; cash is recorded with "Registrar pago". */}
              {alert.code === "balance_due" && unpaidOrder && booking ? (
                <ConfirmOrderPayment booking={booking} orderId={unpaidOrderId} amountLabel={unpaidAmount} onDone={load} />
              ) : alert.section !== "Resumen" && section !== alert.section ? (
                <Button variant="secondary" onClick={() => openSection(alert.section)}>
                  {alert.action}
                </Button>
              ) : null}
            </article>
          ))}
        </section>
      )}
      <div ref={layoutRef} className={`case-layout case-layout--${mobileView}`}>
        <div className="case-mobile-switch" role="tablist" aria-label="Vista del expediente">
          <button
            type="button"
            role="tab"
            aria-selected={mobileView === "main"}
            onClick={() => setMobileView("main")}
          >
            <MessageCircle size={16} aria-hidden="true" />
            {section === "Resumen" ? "Chat" : section}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mobileView === "side"}
            onClick={() => setMobileView("side")}
          >
            <ClipboardList size={16} aria-hidden="true" /> Pagos y notas
          </button>
        </div>
        <div className="case-layout__main">
          {section === "Resumen" ? (
            <CaseChat
              conversationId={conversationId ?? dossier.whatsappConversationId ?? null}
              customerName={dossier.customer.name}
              phone={dossier.customer.phone}
              initialReply={typeof (location.state as { reply?: unknown } | null)?.reply === "string" ? (location.state as { reply: string }).reply : ""}
              empty={
                <div className="case-chat__fallback">
                  <StatePanel
                    state="empty"
                    title={dossier.customer.phone ? "Aún no hay conversación" : "Sin WhatsApp"}
                    description={
                      dossier.customer.phone
                        ? `${dossier.customer.phone} todavía no nos ha escrito. Puedes enviarle el primer mensaje abajo.`
                        : "Este cliente no tiene teléfono registrado."
                    }
                  />
                  {dossier.leads.map((item) => (
                    <div key={item.id} className="case-message">
                      <p>{text(item.last_note, "Solicitud recibida desde el formulario público.")}</p>
                      <small>Solicitud web</small>
                    </div>
                  ))}
                  {dossier.conversations
                    .filter((item) => item.channel !== "whatsapp")
                    .map((item) => (
                      <div key={item.id} className="case-message">
                        <p>{text(item.preview ?? item.subject, "Consulta registrada.")}</p>
                        <small>
                          {copyLabel(item.channel)} · {copyLabel(item.state)}
                        </small>
                      </div>
                    ))}
                </div>
              }
            />
          ) : (
            <div className="case-section-view">
              <div className="case-section-view__bar">
                <Button variant="quiet" icon={ArrowLeft} onClick={() => updateSection("Resumen")}>
                  Volver al chat
                </Button>
                <h2>{section}</h2>
              </div>
              <QuotePanel
                key={`${kind}:${id}`}
                dossier={dossier}
                onChanged={load}
                autoStart={params.get("nueva") === "1"}
                onReserved={(message) => {
                  // Reserved: back to the chat, with the result on top; nothing else to look for.
                  setFeedback(message);
                  updateSection("Resumen");
                }}
              />
            </div>
          )}
        </div>
        <div className="case-layout__side">
          {/* Only what is worked every day: quote, payments, and a note or reminder. The rest is in the case file. */}
          {canQuote && (
            <Button
              icon={FileText}
              variant={section === "Cotizaciones" ? "primary" : "secondary"}
              className="case-side-quote"
              onClick={openQuotes}
            >
              Cotizar{dossier.quotes.length ? ` · ${dossier.quotes.length === 1 ? "1 cotización" : `${dossier.quotes.length} cotizaciones`}` : ""}
            </Button>
          )}
          {booking && dossier.permissions.finance && dossier.bookings[0] && (
            <section className="workspace-card case-side-payments" aria-label="Pagos y comprobantes">
              <h2>Pagos y comprobantes</h2>
              <PaymentSummary
                summary={dossier.bookings[0].payment_summary}
                enabled
                currency={text(dossier.bookings[0].currency, "MXN")}
              />
              <BalanceLink
                summary={dossier.bookings[0].payment_summary as Record<string, unknown> | null}
                service={booking.service}
                date={booking.date}
                time={booking.time}
                customerName={dossier.customer.name}
                phone={dossier.customer.phone}
                conversationId={caseConversation}
                canSend={hasPermission("whatsapp.reply")}
                currency={text(dossier.bookings[0].currency, "MXN")}
              />
              {unpaidOrder && (
                <ConfirmOrderPayment booking={booking} orderId={unpaidOrderId} amountLabel={unpaidAmount} onDone={load} />
              )}
              <BookingActions booking={booking} confirmation={confirmation} paymentSummary={dossier.bookings[0].payment_summary} onlyPayment payRequest={payRequest} />
              <PaymentDocuments
                bookingId={String(dossier.bookings[0].id)}
                service={String(dossier.bookings[0].service_type ?? dossier.service ?? "")}
                summary={dossier.bookings[0].payment_summary as Record<string, unknown> | null}
                conversationId={caseConversation}
                canSend={hasPermission("whatsapp.reply")}
                withoutConfirmation={hasAgreedQuote(dossier)}
              />
              <CaseConfirmation
                dossier={dossier}
                paid={Number((dossier.bookings[0].payment_summary as Record<string, unknown> | null)?.paid_amount ?? 0)}
                conversationId={caseConversation}
                canSend={hasPermission("whatsapp.reply")}
              />
            </section>
          )}
          {/* Before there is a booking: money received by transfer or cash, with or without a quote. */}
          {!booking && dossier.permissions.finance && (
            <CasePayments
              dossier={dossier}
              onRecorded={load}
              onFillData={() => setSheetMode("edit")}
              onReserved={setFeedback}
              conversationId={caseConversation}
              canSend={hasPermission("whatsapp.reply")}
              payRequest={payRequest}
            />
          )}
          {dossier.opportunity && hasPermission("crm.write") && (
            <FollowupEditor
              key={dossier.opportunity.id}
              dossier={dossier}
              onSaved={load}
            />
          )}
        </div>
      </div>
      {/* On the phone: the case file and the payment always at hand, at the bottom. */}
      <nav className="case-mobile-actions" aria-label="Acciones rápidas del expediente">
        <button type="button" onClick={() => openSheet()}>
          <FolderOpen size={18} aria-hidden="true" /> Expediente
        </button>
        {canPay && (
          <button type="button" onClick={() => setPayRequest((value) => value + 1)}>
            <Wallet size={18} aria-hidden="true" /> Registrar pago
          </button>
        )}
      </nav>
      <CaseSheet
        dossier={dossier}
        open={Boolean(sheetMode)}
        startEditing={sheetMode === "edit"}
        focus={sheetFocus}
        onClose={() => setSheetMode("")}
        onSaved={load}
        onOpenQuotes={canQuote ? () => { setSheetMode(""); openQuotes(); } : undefined}
        conversationId={caseConversation}
        canSend={hasPermission("whatsapp.reply")}
        onContactSaved={booking && hasPermission("bookings.edit") ? async (contact) => {
          await executeCommand({
            type: "booking.update_details",
            aggregateId: booking.id,
            expectedVersion: booking.version,
            payload: {
              customer_name: contact.name,
              customer_email: contact.email || booking.email,
              customer_phone: /^\+[1-9]\d{7,14}$/.test(contact.phone) ? contact.phone : booking.phone,
              language: booking.language,
            },
          });
        } : undefined}
        bookingChanges={booking ? (
          <BookingChanges
            booking={booking}
            dossier={dossier}
            onDone={(message) => {
              setFeedback(message);
              load();
            }}
          />
        ) : undefined}
      >
        {(dossier.bookings.length > 0 || dossier.orders.length > 0) && (
          <section className="case-sheet__block case-sheet__block--wide" aria-label="Reserva y pagos" data-section="Reserva y pagos">
            <header><h3>Reserva y pedidos</h3></header>
            <ul className="case-sheet__list">
              {dossier.bookings.map((item) => (
                <li key={`booking-${item.id}`}>
                  <span>
                    <strong>Reserva RES-{String(item.source_id ?? item.id)}</strong>
                    <small>{dateLabel(item.booking_date)} · {text(item.booking_time)} · {text(item.people_count)} personas</small>
                  </span>
                  <StatusBadge tone={item.status === "rejected" ? "danger" : "neutral"}>
                    {item.status === "rejected"
                      ? item.last_error === "confirmation_pdf_generation_failed" ? "PDF con error" : "Envío fallido"
                      : copyLabel(item.status)}
                  </StatusBadge>
                </li>
              ))}
              {dossier.orders.map((item) => (
                <li key={`order-${item.id}`}>
                  <span>
                    <strong>Pedido #{text(item.order_number, item.id)}</strong>
                    <small>
                      {copyLabel(item.status)} · {text(item.payment_method_title)}
                      {dossier.permissions.finance ? ` · ${money(number(item.total), text(item.currency, "MXN"))}` : ""}
                    </small>
                  </span>
                </li>
              ))}
            </ul>
            {booking && (
              <>
                <BookingActions
                  booking={booking}
                  confirmation={confirmation}
                  paymentSummary={dossier.bookings[0]?.payment_summary}
                  // The customer block edits the contact; only flights still edit passengers here.
                  hide={booking.service === "balloon" ? ["addon"] : ["addon", "edit"]}
                />
                <div className="case-actions">
                  {hasPermission("bookings.attend") && (
                    <Button
                      icon={CheckCircle2}
                      disabled={working || !booking.paid || booking.status !== "confirmed" || booking.attended}
                      onClick={() => void attend()}
                    >
                      {booking.attended ? "Asistencia registrada" : "Registrar asistencia"}
                    </Button>
                  )}
                </div>
              </>
            )}
            <p className="report-explainer">Cancelaciones, reembolsos y ajustes financieros se realizan desde WooCommerce.</p>
          </section>
        )}
        {dossier.service !== "school" && (participants.length > 0 || booking) && (
          <section className="case-sheet__block case-sheet__block--wide" aria-label="Participantes" data-section="Participantes">
            <header><h3>Participantes</h3><small>{participants.length} registrados</small></header>
            {participants.length ? (
              <ol className="case-sheet__people">
                {participants.map((item) => (
                  <li key={item.id}>
                    <strong>{text(item.name)}</strong>
                    <small>
                      {copyLabel(item.attendee_type)}
                      {item.weight_kg ? ` · ${text(item.weight_kg)} kg` : ""}
                      {typeof (item.metadata as { phone?: unknown } | null)?.phone === "string" && (item.metadata as { phone: string }).phone
                        ? ` · WhatsApp ${(item.metadata as { phone: string }).phone}`
                        : ""}
                    </small>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="workspace-empty">Los participantes aparecerán al completar la reserva.</p>
            )}
            {booking && hasPermission("bookings.edit") && (
              <p className="report-explainer">Nombres, edades y pesos se cambian con «Editar reserva», en Reserva y pedidos.</p>
            )}
          </section>
        )}
        {(dossier.documents.length > 0 || booking) && (
          <section className="case-sheet__block case-sheet__block--wide" aria-label="Documentos" data-section="Documentos">
            <header><h3>Documentos y confirmaciones</h3></header>
            {dossier.documents.map((item) => (
              <CaseDocument key={item.id} item={item} allowed={hasPermission("confirmations.read")} />
            ))}
            {!dossier.documents.length && <p className="workspace-empty">Aún no hay confirmaciones registradas.</p>}
            {dossier.service === "balloon" && dossier.bookings[0] && hasPermission("confirmations.read") && (
              <LiveConfirmation bookingId={String(dossier.bookings[0].id)} variant="customer" />
            )}
            {dossier.service === "balloon" && dossier.bookings[0] && hasPermission("bookings.read") && (
              <LiveConfirmation bookingId={String(dossier.bookings[0].id)} variant="operator" />
            )}
          </section>
        )}
        {lead && (
          <section className="case-sheet__block case-sheet__block--wide" aria-label="Solicitud web">
            <header><h3>Solicitud web</h3></header>
            <p className="report-explainer">El cliente escribió desde el formulario del sitio.</p>
            <Button variant="secondary" icon={UserRound} onClick={() => setDetail("lead")}>Ver solicitud web</Button>
          </section>
        )}
      </CaseSheet>
      {dossier.opportunity && canChangeStage && (
        <StageDialog
          open={stageOpen}
          opportunity={dossier.opportunity}
          customerName={dossier.customer.name}
          onClose={() => setStageOpen(false)}
          onSaved={(name) => {
            setFeedback(`Etapa cambiada a ${name}.`);
            load();
          }}
        />
      )}
      <BookingDialog
        booking={detail === "booking" ? (booking ?? null) : null}
        onClose={() => {
          setDetail(null);
          load();
        }}
      />
      <OrderDialog
        order={detail === "order" ? (order ?? null) : null}
        onClose={() => setDetail(null)}
      />
      <LeadDialog
        lead={detail === "lead" ? (lead ?? null) : null}
        onClose={() => {
          setDetail(null);
          load();
        }}
      />
      <CustomerDialogHost />
    </section>
  );
}
/** Changing the stage by hand: it moves by itself with the chat, the quote and the payments. */
function StageDialog({
  open,
  opportunity,
  customerName,
  onClose,
  onSaved,
}: {
  open: boolean;
  opportunity: CaseEntity;
  customerName: string;
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const current = String(opportunity.stage ?? "new");
  const [stage, setStage] = useState(current);
  const [lostReason, setLostReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setStage(current);
    setLostReason(typeof opportunity.lost_reason === "string" ? opportunity.lost_reason : "");
    setError("");
  }, [open, current, opportunity.version, opportunity.lost_reason]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || stage === current) return;
    setBusy(true);
    setError("");
    try {
      await crmApi.updateOpportunity(String(opportunity.id), {
        expected_version: Number(opportunity.version),
        stage: stage as CrmStage,
        ...(stage === "lost" ? { lost_reason: lostReason.trim() } : {}),
      });
      onSaved(stageNames[stage] ?? stage);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No se pudo cambiar la etapa.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} size="compact" title="Cambiar etapa" eyebrow={customerName} onClose={onClose}>
      <form className="booking-action-form" onSubmit={(event) => void save(event)}>
        <p>La etapa avanza sola con la conversación, la cotización y los pagos. Cámbiala solo si hace falta.</p>
        <div className="funnel-chips" role="radiogroup" aria-label="Etapa de la venta">
          {manualStages.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={stage === value}
              className="funnel-chip"
              onClick={() => setStage(value)}
            >
              {stageNames[value] ?? value}
            </button>
          ))}
        </div>
        {stage === "lost" && (
          <div className="case-quick__lost">
            <div className="funnel-chips" role="group" aria-label="Motivos frecuentes">
              {lostReasons.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  className="funnel-chip"
                  aria-pressed={lostReason === reason}
                  onClick={() => setLostReason(reason)}
                >
                  {reason}
                </button>
              ))}
            </div>
            <Field label="Motivo del cierre" required value={lostReason} onChange={(e) => setLostReason(e.target.value)} />
          </div>
        )}
        {stage === "won" && stage !== current && (
          <p className="report-explainer">Apartado no registra el dinero: el anticipo se registra con «Registrar pago».</p>
        )}
        {error && <p role="alert" className="case-sheet__error">{error}</p>}
        <Button type="submit" disabled={busy || stage === current || (stage === "lost" && !lostReason.trim())}>
          {busy ? "Guardando…" : "Guardar etapa"}
        </Button>
      </form>
    </Dialog>
  );
}
/** The next action with its "Hecho", a note, and a reminder: the daily follow-up of the sale. */
function FollowupEditor({
  dossier,
  onSaved,
}: {
  dossier: CaseDossier;
  onSaved: () => void;
}) {
  const opportunity = dossier.opportunity!;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [askNext, setAskNext] = useState(false);
  const [feedback, setFeedback] = useState("");
  const run = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !note.trim() || !dossier.customer.id) return;
    setBusy(true);
    setFeedback("");
    try {
      await crmApi.addNote(dossier.customer.id, note.trim(), opportunity.id);
      setNote("");
      setFeedback("Nota guardada.");
      onSaved();
    } catch (caught) {
      setFeedback(caught instanceof Error ? caught.message : "No se pudo guardar la nota.");
    } finally {
      setBusy(false);
    }
  };
  const pending = dossier.followups?.[0];
  return (
    <section className="workspace-card case-quick" aria-label="Nota y recordatorio">
      {pending && (
        <div className="case-quick__next">
          <CalendarClock size={18} aria-hidden="true" />
          <span>
            <small>Próxima acción</small>
            <strong>{text(pending.note)}</strong>
            <small>{dateTime(pending.due_at)}</small>
          </span>
          <Button
            variant="quiet"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setFeedback("");
              try {
                await crmApi.updateFollowup(pending.id, {
                  expected_version: Number(pending.version),
                  status: "completed",
                });
                setFeedback("Hecho.");
                onSaved();
                if (opportunity.stage !== "won" && opportunity.stage !== "lost") setAskNext(true);
              } catch (e) {
                setFeedback(e instanceof Error ? e.message : "No se pudo completar.");
                onSaved();
              } finally {
                setBusy(false);
              }
            }}
          >
            Hecho
          </Button>
        </div>
      )}
      <form className="case-quick__form" onSubmit={(event) => void run(event)}>
        <label className="quote-form-label">
          Nota
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Qué habló el cliente…"
          />
        </label>
        {feedback && <p role="status">{feedback}</p>}
        <div className="case-quick__row">
          <Button variant="secondary" type="submit" disabled={busy || !note.trim()}>
            {busy ? "Guardando…" : "Guardar nota"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            icon={CalendarClock}
            disabled={busy || opportunity.stage === "lost"}
            onClick={() => setAskNext(true)}
          >
            {pending ? "Cambiar recordatorio" : "Recordatorio"}
          </Button>
        </div>
      </form>
      <NextActionDialog
        open={askNext}
        opportunityId={opportunity.id}
        customerName={dossier.customer.name}
        onClose={() => setAskNext(false)}
        onSaved={onSaved}
      />
    </section>
  );
}
const liveConfirmationCopy = {
  customer: {
    title: "Confirmación actual para el cliente",
    detail: "Con el diseño y los datos de hoy. El documento archivado es la copia que se envió.",
    action: "PDF actualizado",
    label: "Descargar la confirmación actual del cliente",
    failure: "No pudimos generar la confirmación actual.",
  },
  operator: {
    title: "Confirmación para el globopuerto",
    detail: "Pasajeros, pesos, extras y punto de encuentro, sin precios.",
    action: "PDF sin precios",
    label: "Descargar confirmación sin precios para el globopuerto",
    failure: "No pudimos generar el PDF para el globopuerto.",
  },
} as const;

/** The confirmation generated now from the live booking: the customer's copy, or the price-free one for the balloon port. */
function LiveConfirmation({ bookingId, variant }: { bookingId: string; variant: keyof typeof liveConfirmationCopy }) {
  const copy = liveConfirmationCopy[variant];
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    setError("");
    try {
      if (!useDevelopmentFixtures)
        await (variant === "customer" ? commerceApi.downloadCurrentConfirmation(bookingId) : commerceApi.downloadOperatorConfirmation(bookingId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : copy.failure);
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="workspace-row">
      <div>
        <strong>{copy.title}</strong>
        <small>{copy.detail}</small>
        {error && <p role="alert">{error}</p>}
      </div>
      <Button variant="secondary" icon={Download} disabled={downloading} onClick={() => void download()} aria-label={copy.label}>
        {downloading ? "Generando PDF…" : copy.action}
      </Button>
    </div>
  );
}
function CaseDocument({
  item,
  allowed,
}: {
  item: CaseEntity;
  allowed: boolean;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const download = async () => {
    if (downloading || !allowed || item.document_available !== true) return;
    setDownloading(true);
    setError("");
    try {
      await erpApi.downloadConfirmationDocument(item.id);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "No pudimos recuperar el PDF archivado.",
      );
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="workspace-row">
      <div>
        <strong>Documento de confirmación #{item.id} · copia enviada</strong>
        {item.status === "rejected" && (
          <p>
            {item.last_error === "confirmation_pdf_generation_failed"
              ? "No se pudo generar el PDF. Este intento no llegó a enviar el correo; el pago y la reserva conservan sus estados."
              : "El envío de la confirmación no se completó. Revisa el intento antes de reintentar."}
          </p>
        )}
        <small>
          {copyLabel(item.channel)} · {text(item.recipient)} ·{" "}
          {dateTime(item.accepted_at ?? item.attempted_at)}
        </small>
        {error && <p role="alert">{error}</p>}
      </div>
      <StatusBadge tone={item.status === "rejected" ? "danger" : "neutral"}>
        {item.status === "rejected"
          ? item.last_error === "confirmation_pdf_generation_failed"
            ? "PDF con error"
            : "Envío fallido"
          : item.channel === "email" && item.status === "accepted"
            ? "Aceptado por SMTP"
            : copyLabel(item.status)}
      </StatusBadge>
      {allowed && item.document_available === true ? (
        <Button
          variant="secondary"
          disabled={downloading}
          onClick={() => void download()}
          aria-label={`Descargar PDF de confirmación #${item.id}`}
        >
          {downloading ? "Recuperando PDF…" : "Descargar PDF"}
        </Button>
      ) : (
        <span>PDF no disponible</span>
      )}
    </div>
  );
}
function leadFromCase(row: CaseEntity, dossier: CaseDossier): Lead | null {
  const service = row.service_type ?? dossier.service;
  const status = String(row.status ?? "");
  const version = Number(row.source_version ?? row.version);
  const created = [row.source_created_at, row.created_at].find(
    (value) =>
      typeof value === "string" && value && !Number.isNaN(Date.parse(value)),
  );
  if (
    !["balloon", "tour", "school"].includes(String(service)) ||
    !["new", "contacted", "qualified", "lost"].includes(status) ||
    !Number.isSafeInteger(version) ||
    version < 0 ||
    typeof created !== "string"
  )
    return null;
  const requestedDate =
    typeof row.requested_date === "string" && row.requested_date
      ? row.requested_date.slice(0, 10)
      : undefined;
  const people = number(row.people_count),
    estimated = number(row.estimated_total);
  return {
    id: row.id,
    customer: text(row.customer_name, dossier.customer.name),
    email: text(row.customer_email, dossier.customer.email ?? ""),
    phone: text(row.customer_phone, dossier.customer.phone ?? ""),
    interest: text(
      row.interest,
      text(row.last_note, serviceNames[service as Lead["service"]]),
    ),
    service: service as Lead["service"],
    ...(requestedDate ? { requestedDate } : {}),
    ...(people !== null ? { peopleCount: people } : {}),
    ...(estimated !== null && dossier.permissions.finance
      ? { estimatedTotal: estimated }
      : {}),
    currency: text(row.currency, "MXN"),
    createdAt: created,
    status: status as Lead["status"],
    ...(typeof row.follow_up_at === "string" && row.follow_up_at
      ? { nextFollowupAt: row.follow_up_at }
      : {}),
    notes:
      typeof row.last_note === "string" && row.last_note ? [row.last_note] : [],
    version,
  };
}
function PaymentSummary({
  summary,
  enabled,
  currency,
}: {
  summary: unknown;
  enabled: boolean;
  currency: string;
}) {
  if (!enabled || !summary || typeof summary !== "object") return null;
  const value = summary as Record<string, unknown>;
  return (
    <>
      <dl className="case-details">
        <Fact label="Total contratado">
          {money(number(value.contract_total), currency)}
        </Fact>
        <Fact label="Recibido">
          {money(number(value.paid_amount), currency)}
        </Fact>
        <Fact label="Saldo pendiente">
          {money(number(value.balance_due), currency)}
        </Fact>
        <Fact label="Vencimiento del saldo">
          {dateTime(value.balance_due_at_iso ?? value.balance_due_at_local)}
        </Fact>
        {(number(value.reservation_fee) ?? 0) > 0 && (
          <Fact label="Apartado de fecha">
            {money(number(value.reservation_fee_paid) ?? 0, currency)} de {money(number(value.reservation_fee), currency)}
          </Fact>
        )}
      </dl>
      {Array.isArray(value.manual_payments) && value.manual_payments.length > 0 && (
        <ul className="manual-payments" aria-label="Pagos registrados">
          {(value.manual_payments as Record<string, unknown>[]).map((entry, index) => (
            <li key={String(entry.id ?? index)}>
              <span>{dateLabel(entry.paid_on)} · {entry.method === "transfer" ? "Transferencia" : "Efectivo"}{entry.kind === "reservation_fee" ? " · Apartado" : ""}{entry.note ? ` · ${String(entry.note)}` : ""}</span>
              <strong>{money(number(entry.amount), currency)}</strong>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
function Fact({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon?: LucideIcon;
  children: ReactNode;
}) {
  return (
    <div>
      <dt>
        {Icon && <Icon size={14} aria-hidden="true" />}
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}
function text(value: unknown, fallback = "Por definir") {
  return (typeof value === "string" && value) || typeof value === "number"
    ? String(value)
    : fallback;
}
function number(value: unknown) {
  return typeof value === "number" ||
    (typeof value === "string" &&
      value.trim() &&
      Number.isFinite(Number(value)))
    ? Number(value)
    : null;
}
function dateLabel(value: unknown) {
  return typeof value === "string" && value ? formatDate(value) : "Por definir";
}
function dateTime(value: unknown) {
  return typeof value === "string" && value && !Number.isNaN(Date.parse(value))
    ? formatDateTime(value)
    : "Sin fecha registrada";
}

/**
 * Date and people in the case header. Before a booking exists they belong to the sale and can be
 * corrected here (they prefill the quote); after it, WordPress owns them and they are read-only.
 */
/** The service pill doubles as a picker, so a sale filed under the wrong service can be moved. */
function ServicePicker({
  service,
  opportunity,
  onSaved,
}: {
  service: ServiceType | null;
  opportunity: { id: string; version: number };
  onSaved: (serviceName: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const Icon = service ? serviceIcons[service] : Compass;
  const change = async (next: ServiceType) => {
    if (next === service || busy) return;
    setBusy(true);
    setError("");
    try {
      await crmApi.updateOpportunityDetails(opportunity.id, { expected_version: opportunity.version, service_type: next });
      onSaved(serviceNames[next]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No pudimos cambiar el servicio.");
    } finally {
      setBusy(false);
    }
  };
  // A button with its own list (not a native select): Safari did not open the select inside the chip.
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === "Escape" : !menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  return (
    <>
      <span ref={menuRef} className={`case-hero__service case-hero__service--${service ?? "none"} case-hero__service--picker`}>
        <button type="button" className="case-hero__service-button" aria-haspopup="menu" aria-expanded={open} aria-label={`Servicio de la venta: ${service ? serviceNames[service] : "por definir"}`} disabled={busy} onClick={() => setOpen((value) => !value)}>
          <Icon size={15} aria-hidden="true" />
          {service ? serviceNames[service] : "Servicio por definir"}
          <ChevronDown size={14} aria-hidden="true" />
        </button>
        {open && (
          <span className="case-hero__service-menu" role="menu" aria-label="Cambiar servicio">
            {(Object.keys(serviceNames) as ServiceType[]).map((value) => {
              const ItemIcon = serviceIcons[value];
              return (
                <button key={value} type="button" role="menuitemradio" aria-checked={value === service} onClick={() => { setOpen(false); void change(value); }}>
                  <ItemIcon size={15} aria-hidden="true" /> {serviceNames[value]}
                </button>
              );
            })}
          </span>
        )}
      </span>
      {error && <p role="alert" className="case-hero__error">{error}</p>}
    </>
  );
}

function TripFacts({
  date,
  people,
  school = false,
  teachers = null,
  companions = null,
  precision = "day",
  opportunity,
  onSaved,
}: {
  date: string;
  people: number | null;
  /** A school group shows students, teachers and companions apart. */
  school?: boolean;
  teachers?: number | null;
  companions?: number | null;
  precision?: DatePrecision;
  opportunity: { id: string; version: number } | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftDate, setDraftDate] = useState(date);
  const [draftPrecision, setDraftPrecision] = useState<DatePrecision>(precision);
  const [draftPeople, setDraftPeople] = useState(people ? String(people) : "");
  const [draftTeachers, setDraftTeachers] = useState(teachers == null ? "" : String(teachers));
  const [draftCompanions, setDraftCompanions] = useState(companions == null ? "" : String(companions));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const open = () => {
    setDraftDate(date);
    setDraftPrecision(precision);
    setDraftPeople(people ? String(people) : "");
    setDraftTeachers(teachers == null ? "" : String(teachers));
    setDraftCompanions(companions == null ? "" : String(companions));
    setError("");
    setEditing(true);
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!opportunity || busy) return;
    setBusy(true);
    setError("");
    try {
      await crmApi.updateOpportunityDetails(opportunity.id, {
        expected_version: opportunity.version,
        requested_date: draftDate || null,
        ...(draftDate ? { requested_date_precision: draftPrecision } : {}),
        people_count: Number(draftPeople) > 0 ? Number(draftPeople) : null,
        ...(school
          ? {
              teachers_count: draftTeachers === "" ? null : Math.max(0, Math.round(Number(draftTeachers))),
              companions_count: draftCompanions === "" ? null : Math.max(0, Math.round(Number(draftCompanions))),
            }
          : {}),
      });
      setEditing(false);
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No pudimos guardar los datos del viaje.");
    } finally {
      setBusy(false);
    }
  };
  const facts = (
    <>
      <CalendarDays size={16} aria-hidden="true" />
      {!date ? "Fecha por definir" : precision === "month" ? `${monthText(date)} · día por confirmar` : formatDate(date)}
      <span className="case-hero__sep" aria-hidden="true">·</span>
      <Users size={16} aria-hidden="true" />
      {school
        ? [
            people ? `${people} alumnos` : "Alumnos por definir",
            ...(teachers ? [`${teachers} ${teachers === 1 ? "profesor" : "profesores"}`] : []),
            ...(companions ? [`${companions} ${companions === 1 ? "acompañante" : "acompañantes"}`] : []),
          ].join(" · ")
        : people ? `${people} personas` : "Personas por definir"}
    </>
  );
  if (!opportunity) return <li>{facts}</li>;
  if (!editing)
    return (
      <li>
        <button type="button" className="case-hero__edit" onClick={open} aria-label={school ? "Editar fecha y grupo" : "Editar fecha y personas"}>
          {facts}
          <PenLine size={14} aria-hidden="true" />
        </button>
      </li>
    );
  return (
    <li className="case-hero__editor">
      <form onSubmit={(event) => void save(event)}>
        <TripDateField label="Fecha del viaje" name="trip_date" value={draftDate} precision={draftPrecision} onChange={(next, nextPrecision) => { setDraftDate(next); setDraftPrecision(nextPrecision); }} />
        <Field label={school ? "Alumnos" : "Personas"} type="number" min="1" max="5000" value={draftPeople} onChange={(e) => setDraftPeople(e.target.value)} />
        {school && <Field label="Profesores" type="number" min="0" max="1000" value={draftTeachers} onChange={(e) => setDraftTeachers(e.target.value)} />}
        {school && <Field label="Acompañantes" type="number" min="0" max="1000" value={draftCompanions} onChange={(e) => setDraftCompanions(e.target.value)} />}
        <Button type="submit" disabled={busy}>{busy ? "Guardando…" : "Guardar"}</Button>
        <Button type="button" variant="quiet" onClick={() => setEditing(false)}>Cancelar</Button>
      </form>
      {error && <p role="alert">{error}</p>}
    </li>
  );
}

